/**
 * Race a promise against a timeout. If the timeout fires first, the returned
 * promise rejects with a `TimeoutError` (a plain Error with name === 'TimeoutError').
 *
 * IMPORTANT: This does NOT cancel the underlying work. For Firestore reads in
 * particular, `getDocs` cannot be cancelled — the SDK will keep retrying in
 * the background even after the timeout fires. Callers must rely on their own
 * supersede / token logic to discard zombie results when they eventually
 * resolve. See `loadFeaturedPedals` in LatestPedals.jsx for an example.
 *
 * @template T
 * @param {Promise<T>} promise   The work to race.
 * @param {number} timeoutMs     Milliseconds to wait before timing out.
 * @param {string} [label]       Optional label included in the error message
 *                               (useful for debugging which call timed out).
 * @returns {Promise<T>}
 */
export const withTimeout = (promise, timeoutMs, label = 'operation') => {
  let timer;
  const timeoutPromise = new Promise((_, reject) => {
    timer = setTimeout(() => {
      const err = new Error(`Timed out after ${timeoutMs}ms: ${label}`);
      err.name = 'TimeoutError';
      reject(err);
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    clearTimeout(timer);
  });
};

export const isTimeoutError = (err) =>
  !!err && (err.name === 'TimeoutError' || /timed out/i.test(err.message || ''));
